import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AppLayout";
import { UpgradeGate } from "@/components/UpgradeGate";
import { LetterheadCard } from "@/components/LetterheadCard";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { INDUSTRIES } from "@/lib/constants";
import { toast } from "sonner";

function NewProposalDialog({ open, onOpenChange, grants, initialGrant }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ grant_id: initialGrant || "none", title: "", org_name: "", org_description: "", project_summary: "", amount_requested: "", industry: "Small Business" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/proposals", { ...f, grant_id: f.grant_id === "none" ? null : f.grant_id });
      navigate(`/dashboard/proposals/${data.proposal_id}?autogen=1`);
    } catch (e2) {
      toast.error(formatErr(e2));
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-proposal-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New grant proposal</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Target grant</Label>
            <Select value={f.grant_id} onValueChange={(v) => setF({ ...f, grant_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="proposal-grant-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General proposal (no specific grant)</SelectItem>
                {grants.map((g) => <SelectItem key={g.grant_id} value={g.grant_id}>{g.grant.title.slice(0, 70)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Project title</Label><Input required className="mt-1.5" value={f.title} onChange={set("title")} data-testid="proposal-title-input" /></div>
          <div><Label>Organization name</Label><Input required className="mt-1.5" value={f.org_name} onChange={set("org_name")} data-testid="proposal-org-name-input" /></div>
          <div><Label>Industry</Label>
            <Select value={f.industry} onValueChange={(v) => setF({ ...f, industry: v })}>
              <SelectTrigger className="mt-1.5" data-testid="proposal-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>About your organization</Label><Textarea required rows={3} className="mt-1.5" placeholder="Mission, history, who you serve, key achievements" value={f.org_description} onChange={set("org_description")} data-testid="proposal-org-description-input" /></div>
          <div className="md:col-span-2"><Label>Project summary</Label><Textarea required rows={4} className="mt-1.5" placeholder="What will the funding do, for whom, and what will change?" value={f.project_summary} onChange={set("project_summary")} data-testid="proposal-project-summary-input" /></div>
          <div><Label>Amount requested</Label><Input className="mt-1.5" placeholder="$50,000" value={f.amount_requested} onChange={set("amount_requested")} data-testid="proposal-amount-input" /></div>
          <div className="flex items-end"><Button type="submit" disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="proposal-create-submit-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create & generate</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Proposals() {
  const { hasPkg } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [grants, setGrants] = useState([]);
  const [open, setOpen] = useState(!!params.get("grant"));
  const allowed = hasPkg("proposal_writer");
  useEffect(() => {
    api.get("/proposals").then((r) => setItems(r.data));
    api.get("/grants/saved").then((r) => setGrants(r.data));
  }, []);
  return (
    <div>
      <PageHeader eyebrow="Area 01 · Proposal Writer" title="Proposal documents"
        action={allowed && <Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="new-proposal-button"><Plus className="h-4 w-4 mr-2" />New proposal</Button>}>
        Generate complete, funder-ready proposals — then edit, download or send for certified human review.
      </PageHeader>
      {!allowed ? (
        <UpgradeGate pkg="proposal_writer" title="Unlock the Proposal Writer">Generate full grant proposals with cover letter, need statement, objectives, timeline, evaluation and budget justification — tailored to the grant you pick.</UpgradeGate>
      ) : !items ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="proposals-empty"><FileText className="h-8 w-8 mx-auto text-slate-300" /><p className="mt-4 text-slate-500">No proposals yet. Create your first one.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <button key={p.proposal_id} onClick={() => navigate(`/dashboard/proposals/${p.proposal_id}`)} data-testid={`proposal-card-${p.proposal_id}`} className="card-lift text-left bg-white border border-slate-200 rounded-lg p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{new Date(p.updated_at).toLocaleDateString()}</div>
              <h3 className="font-display text-xl font-bold text-slate-900 mt-2">{p.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{p.org_name}{p.grant ? ` → ${p.grant.title}` : ""}</p>
            </button>
          ))}
        </div>
      )}
      {allowed && <NewProposalDialog open={open} onOpenChange={setOpen} grants={grants} initialGrant={params.get("grant")} />}
      <LetterheadCard />
    </div>
  );
}
